import { useStates } from '@tiger/hooks';
import { Button, Input } from 'antd';
import React, { useEffect, useRef } from 'react';
import { connect, Form } from 'react-antd-formutil';
import FormItem from 'components/FormItem';
import Modal from 'components/Modal';
import Toast from 'components/Toast';
import { Collapse } from 'components/Transition';
import useCountdown from 'hooks/useCountDown';
import security from 'stores/security';
import classlist from 'utils/classlist';
import GeeCaptcha, { CaptchaProps } from '../Captcha';

import './style.scss';

// wiki https://wiki.tigerbrokers.net/pages/viewpage.action?pageId=20615776 @江帅
export type ProcessTypes =
    | 'signup'
    | 'fast_signin'
    | 'modify_email'
    | 'modify_phone'
    | 'modify_login_password'
    | 'modify_trade_password'
    | 'reset_login_password'
    | 'reset_trade_password'
    | 'bind_email'
    | 'bind_phone'
    | 'account_manage'
    | 'internal_funds_transfer'
    | 'internal_position_transfer'
    | 'personal_withdraw'
    | 'withdrawal'
    | 'fund'
    | 'retrieve_ib_password'
    | 'bank_card_application'
    | 'update_login_password'
    | 'verify_code_signin'
    | 'mfa_enable';

export interface VerifyCodeSendProps extends Pick<CaptchaProps, 'errorHandle'> {
    platform?: 'portal' | 'oauth';
    type?: 'phone' | 'email';
    name?: string;
    username?: string; // 账号，需要传递对应 type 的类型的账号值，如果是手机号的话，还需要额外传递telcode。登录状态下可以传递username={null}，则为给当前登录用户的绑定账号发送
    logined?: boolean; // 是否登录模式，如果传true，调用发验证码接口时将不会传递phone或者email，默认给当前登录用户发送
    process: ProcessTypes; // 短信用途
    tel_code?: string;
    timeout?: number;
    label?: React.ReactNode;
    disabled?: boolean;
    onSend?(): void;
    onError?(error: Error): void;
    sendBtnText?: string;
    placeholder?: string;
    maxLength?: number;
    line?: boolean; // 默认为true，即底部线框模式
}

export interface VerifyCodeSendState {
    showVoice: boolean;
}

const VerifyCodeSend: React.FC<VerifyCodeSendProps> = props => {
    const {
        username,
        placeholder = __('Verification Code'),
        disabled: propDisabled,
        timeout = 60,
        platform = 'oauth',
        logined,
        type = 'phone',
        name = 'verify_code',
        tel_code = '1',
        label = null,
        process,
        maxLength = 6,
        sendBtnText = __('Get verification code'),
        line = true,
        onSend,
        errorHandle
    } = props;
    const verifyModal = useRef<any>(null);
    const { isCounting: smsCounting, countdown: smsCountdown, start: startSmsCountdown } = useCountdown(timeout);
    const { countdown: voiceCountdown, start: startVoiceCountdown, isCounting: voiceCounting } = useCountdown(timeout);
    const [state, setState] = useStates({
        showVoice: false,
        hasSend: false
    });
    // 后端返回的 voice_wait_seconds 原始值，-1=不可用, 0=立即可用, >0=等待秒数
    const voiceWaitSecondsRef = useRef(timeout);
    const isCountingRef = useRef(smsCounting || voiceCounting);

    isCountingRef.current = smsCounting || voiceCounting;

    const sendMsg = () => {
        createVerify();
    };

    const sendVoiceMsg = () => {
        createVerify(true);
    };

    const createVerify = (voice = false) => {
        Toast.loading(true, __('Loading...'));

        (verifyModal.current = Modal.open({
            // backdrop: 'static',
            component: props => (
                <Form<any>
                    children={$formutil => {
                        return (
                            <div className="modal-sendmsg-verify">
                                <h3 className="title">{__('Identity verification')}</h3>
                                <div className="form-group">
                                    <GeeCaptcha
                                        isVoice={voice}
                                        onError={() => props.dismiss()}
                                        onSuccess={() => {
                                            const { $params } = $formutil.$new();

                                            props.close($params);
                                        }}
                                        action={process}
                                        errorHandle={errorHandle}
                                    />
                                </div>
                                <footer className="mt-3 justify-content-center">
                                    <Button
                                        type="default"
                                        size="large"
                                        onClick={() => {
                                            props.dismiss!();
                                        }}>
                                        {__('Cancel')}
                                    </Button>
                                    <Button
                                        type="primary"
                                        size="large"
                                        className="ml-3"
                                        onClick={() => {
                                            const { $invalid, $getFirstError, $params } = $formutil;

                                            if ($invalid) {
                                                Toast.show($getFirstError());
                                            } else {
                                                props.close($params);
                                            }
                                        }}>
                                        {__('Submit')}
                                    </Button>
                                </footer>
                            </div>
                        );
                    }}
                />
            ),
            wrapClassName: 'captcha-modal-wrap'
        })).result
            .then(params => {
                sendCode(params, voice);
            })
            .catch(() => {});
    };

    const sendCode = async ($params, voice = false) => {
        Toast.loading(true, __('Sending...'));

        try {
            const params = {
                process: process,
                tel_code: tel_code,
                voice: voice ? '1' : '0', // oauth方式
                type: voice ? 'voice' : '', // portal 方式

                ...$params
            };

            if (!logined) {
                params[type!] = username;
            }

            const resp = await security.sendSMS(params, platform, type);

            // 根据 voice 参数启动对应倒计时
            if (voice) {
                startVoiceCountdown();
            } else {
                startSmsCountdown();
            }

            onSend?.();

            setState({
                hasSend: true
            });

            // 控制是否展示语音验证码字段
            const voiceWaitSeconds = resp.data?.voice_wait_seconds ?? timeout;

            // 仅短信接口返回 voice_wait_seconds，语音接口不返回，一旦设置则保持
            // 如果接口明确返回 -1，则需要覆盖，来隐藏语音验证码按钮
            if ((!voice && !showVoice) || voiceWaitSeconds === -1) {
                voiceWaitSecondsRef.current = voiceWaitSeconds;
                // 0 表示立即可用直接显示，-1 表示不可用需要隐藏
                setState({ showVoice: voiceWaitSeconds === 0 });
            }
        } catch (error) {
            if (props.onError) {
                props.onError(error);
            } else {
                Toast.show(error);
            }
        }

        Toast.loading(false);
    };

    // 短信倒计时过程中，检查是否到了显示语音按钮的时刻
    useEffect(() => {
        if (voiceWaitSecondsRef.current !== -1 && timeout - smsCountdown >= voiceWaitSecondsRef.current) {
            setState({ showVoice: true });
        }
    }, [smsCountdown, timeout, setState]);

    // 区号或手机号变化时，若不处于倒计时中，重置语音验证码展示状态
    useEffect(() => {
        if (!isCountingRef.current) {
            setState({ showVoice: false });
        }
    }, [username, tel_code, setState]);

    const { showVoice } = state;
    const hasError = !logined && !username;
    const smsDisabled = hasError || smsCounting || propDisabled;
    const voiceDisabled = hasError || voiceCounting || propDisabled;
    const labelNode =
        typeof label === 'string' ? (
            <div className="ant-form-item-label">
                <label>{label}</label>
            </div>
        ) : (
            label
        );

    return (
        <div className="verify-code-root">
            {labelNode}
            <div
                className={classlist('verify-container', {
                    'has-line': line
                })}>
                <FormItem
                    name={name}
                    isError
                    maxLength={6}
                    required
                    $parser={(viewValue, $setViewValue) => $setViewValue(viewValue.slice(0, maxLength))}
                    $validators={{ isError: () => true }}
                    validMessage={{
                        required: __('Please enter the verification code')
                    }}
                    line={line}>
                    <Input placeholder={placeholder} maxLength={maxLength} />
                </FormItem>
                <button
                    className={classlist('sms-code-btn', {
                        line: line
                    })}
                    disabled={smsDisabled}
                    onClick={ev => {
                        ev.preventDefault();
                        sendMsg();
                    }}>
                    {smsCounting ? `${__('sent')} (${smsCountdown}s）` : sendBtnText}
                </button>
            </div>
            {/* 邮箱验证不需要语音验证 */}
            <Collapse in={showVoice && type === 'phone'}>
                <div className="form-group mb-2">
                    <Button type="default" block disabled={voiceDisabled} onClick={sendVoiceMsg}>
                        {voiceCounting ? `${__('sent')}（${voiceCountdown}s）` : __('Send a voice verification code')}
                    </Button>
                </div>
            </Collapse>
        </div>
    );
};

export default connect(VerifyCodeSend);
